import React, { useState, useEffect, useCallback } from 'react';
import { X, AlertCircle } from 'lucide-react';

const PaymentModal = ({ apiCall, families, selectedYear: initialPayYear, onClose, onSuccess, showError }) => {
  // Each module is paid in the year it still OWES (from the family's ledger):
  // prefer the app's year, else the most recent prior year with a balance.
  // moduleYears maps module -> that year (absent = nothing owed → not shown),
  // so credits/paid modules never appear and each payment is tagged correctly.
  const [moduleYears, setModuleYears] = useState({});
  // Every (module, year) the family still owes, from the ledger — the cashier
  // sees the full breakdown and clicks one to pay it (payYear = its year).
  const [owedItems, setOwedItems] = useState([]);
  const [payYear, setPayYear] = useState(null);
  const [paymentData, setPaymentData] = useState({
    familyId: '',
    module: 'Afterschool',
    amount: '',
    paymentMethod: 'Cash',
    paymentDate: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [familySearch, setFamilySearch] = useState('');
  const [, setFilteredFamilies] = useState([]);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [enrollmentSummary, setEnrollmentSummary] = useState(null);
  const [validationWarning, setValidationWarning] = useState('');
  const [oldDaycareBalance, setOldDaycareBalance] = useState(0); // ✅ Add state for old Daycare balances
  
  // Unified search state
  const [searchResults, setSearchResults] = useState({ families: [], groups: [] });
  const [selectedType, setSelectedType] = useState(null); // 'family' or 'group'
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupTrips, setGroupTrips] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);

  const handleFamilySearch = async (searchTerm) => {
    setFamilySearch(searchTerm);
    setValidationWarning('');
    
    if (searchTerm.length >= 2) {
      // Search families
      const familyResults = families.filter(f => {
        const familyId = (f.FamilyID || '').toLowerCase();
        const fatherName = (f.FatherName || '').toLowerCase();
        const motherName = (f.MotherName || '').toLowerCase();
        const phone = (f.PrimaryPhone || '').toLowerCase();
        const search = searchTerm.toLowerCase();
        return familyId.includes(search) || 
               fatherName.includes(search) || 
               motherName.includes(search) ||
               phone.includes(search);
      });
      
      // Search rental groups
      let groupResults = [];
      try {
        groupResults = await apiCall(`/bus/rental-groups/search?searchTerm=${searchTerm}`);
      } catch (error) {
        console.error('Error searching groups:', error);
      }
      
      setSearchResults({
        families: familyResults,
        groups: groupResults || []
      });
      setFilteredFamilies([]); // Clear old state
      
    } else {
      setSearchResults({ families: [], groups: [] });
      setFilteredFamilies([]);
    }
  };

  const loadEnrollmentSummary = useCallback(async (familyId, preferYears = {}) => {
    try {
      // Per-module owed year from the central ledger: pay each module in the
      // year it still owes (prefer the app year, else the most recent prior
      // year with a positive balance). Modules with no positive balance (paid
      // or in credit) resolve to null → skipped, so they never show.
      let ledger = [];
      try { ledger = await apiCall(`/families/${familyId}/balances`) || []; } catch (e) { ledger = []; }
      const owedYearFor = (mod) => {
        const yrs = (Array.isArray(ledger) ? ledger : [])
          .filter((r) => r.Module === mod && Number(r.Balance) > 0)
          .map((r) => r.AcademicYear);
        if (!yrs.length) return null;
        return yrs.slice().sort()[0]; // oldest owed year — settle oldest first
      };
      // Full breakdown: every (module, year) still owed — the cashier sees them
      // all and can pay any one.
      const KNOWN = ['Afterschool', 'Bus', 'Activities', 'Daycare', 'ArtSchool'];
      setOwedItems(
        (Array.isArray(ledger) ? ledger : [])
          .filter((r) => KNOWN.includes(r.Module) && Number(r.Balance) > 0)
          .map((r) => ({ module: r.Module, year: r.AcademicYear, balance: Number(r.Balance) }))
          .sort((a, b) => a.module.localeCompare(b.module) || b.year.localeCompare(a.year))
      );

      const years = {
        Afterschool: owedYearFor('Afterschool'),
        Bus: owedYearFor('Bus'),
        Activities: owedYearFor('Activities'),
        Daycare: owedYearFor('Daycare'),
        ArtSchool: owedYearFor('ArtSchool'),
      };
      // A clicked breakdown row overrides the module's default year.
      Object.assign(years, preferYears);
      setModuleYears(years);

      // Afterschool
      let afterschoolEnrollments = [];
      if (years.Afterschool) {
        try { afterschoolEnrollments = await apiCall(`/afterschool/enrollments?academicYear=${years.Afterschool}&familyId=${familyId}`) || []; } catch (e) { /* module inaccessible */ }
      }

      // Bus
      let busEnrollments = [];
      if (years.Bus) {
        try { busEnrollments = await apiCall(`/bus/enrollments?academicYear=${years.Bus}&familyId=${familyId}`) || []; } catch (e) { /* module inaccessible */ }
      }

      // Activities
      let activitiesEnrollments = [];
      if (years.Activities) {
        try { activitiesEnrollments = await apiCall(`/activities/enrollments?academicYear=${years.Activities}&familyId=${familyId}`) || []; } catch (e) { /* module inaccessible */ }
      }

      // Daycare (charges live in its owed year)
      let daycareData = { enrollments: [], charges: [], totals: {} };
      if (years.Daycare) {
        try {
          const enrollments = await apiCall(`/daycare/enrollments?academicYear=${years.Daycare}&familyId=${familyId}`);
          const chargesData = await apiCall(`/daycare/charges-for-payment?familyId=${familyId}&academicYear=${years.Daycare}`);
          daycareData = {
            enrollments: (enrollments || []).filter(e => e.Status === 'Active'),
            charges: chargesData.allCharges || [],
            chargesByType: chargesData.charges || { oldBalance: [], additional: [], monthly: [] },
            totals: chargesData.totals || { oldBalance: 0, additional: 0, monthly: 0, total: 0 }
          };
          setOldDaycareBalance(daycareData.totals.oldBalance || 0);
        } catch (e) { daycareData = { enrollments: [], charges: [], totals: {} }; setOldDaycareBalance(0); }
      } else { setOldDaycareBalance(0); }

      // Art School
      let artSchoolBalance = 0;
      let artSchoolEnrollments = [];
      if (years.ArtSchool) {
        try {
          const artEnrollments = await apiCall(`/artschool/enrollments?familyID=${familyId}&academicYear=${years.ArtSchool}&status=Active`);
          artSchoolEnrollments = artEnrollments || [];
          artSchoolBalance = artSchoolEnrollments.reduce((sum, e) => sum + (parseFloat(e.BalanceRemaining) || 0), 0);
        } catch (e) { artSchoolBalance = 0; }
      }

      const activitiesWithBalance = activitiesEnrollments.filter(e =>
        e.Status === 'Active' && (e.BalanceRemaining || 0) > 0
      );

      setEnrollmentSummary({
        afterschool: afterschoolEnrollments.filter(e => e.Status === 'Active'),
        bus: busEnrollments,
        activities: activitiesWithBalance,
        daycare: daycareData,
        artSchool: { enrollments: artSchoolEnrollments, balance: artSchoolBalance }
      });
    } catch (error) {
      console.error('Error loading enrollment summary:', error);
      setEnrollmentSummary({
        afterschool: [], bus: [], activities: [],
        daycare: { enrollments: [], charges: [], totals: {} },
        artSchool: { enrollments: [], balance: 0 }
      });
    }
  }, [apiCall]);

  const selectFamily = async (family) => {
    setSelectedType('family');
    setSelectedFamily(family);
    setSelectedGroup(null);
    setGroupTrips([]);
    setSelectedTrip(null);
    setPaymentData({
      ...paymentData,
      familyId: family.FamilyID
    });
    setFamilySearch(`${family.FamilyID} - ${family.FatherName}`);
    setSearchResults({ families: [], groups: [] });
    setFilteredFamilies([]);
    setPayYear(null);
    setOwedItems([]);
    await loadEnrollmentSummary(family.FamilyID);
  };

  // Cashier clicked a specific (module, year) balance in the breakdown: target
  // that module + year and reload its data for that year.
  const pickOwed = async (item) => {
    setPaymentData((pd) => ({ ...pd, module: item.module, amount: String(item.balance) }));
    setPayYear(item.year);
    if (selectedFamily) await loadEnrollmentSummary(selectedFamily.FamilyID, { [item.module]: item.year });
  };

  const selectGroup = async (group) => {
    setSelectedType('group');
    setSelectedGroup(group);
    setSelectedFamily(null);
    setEnrollmentSummary(null);
    setFamilySearch(`${group.GroupName} (GROUP)`);
    setSearchResults({ families: [], groups: [] });
    setFilteredFamilies([]);
    
    // Load unpaid/partial trips for this group
    try {
      const trips = await apiCall(`/bus/rental-trips?groupId=${group.GroupID}`);
      const unpaidTrips = (trips || []).filter(t => 
        t.PaymentStatus === 'Unpaid' || t.PaymentStatus === 'Partial'
      );
      setGroupTrips(unpaidTrips);
    } catch (error) {
      console.error('Error loading trips:', error);
      setGroupTrips([]);
    }
  };

  const selectTrip = (trip) => {
    setSelectedTrip(trip);
    setPaymentData({
      ...paymentData,
      module: 'BusRental',
      amount: trip.BalanceRemaining?.toFixed(2) || ''
    });
  };

  useEffect(() => {
    // Skip validation for rental groups
    if (selectedType === 'group') {
      setValidationWarning('');
      return;
    }
    
    if (enrollmentSummary && paymentData.module) {
      if (paymentData.module === 'Afterschool' && enrollmentSummary.afterschool.length === 0) {
        setValidationWarning('⚠️ Warning: This family has no active Afterschool enrollments. You cannot record an Afterschool payment without an active enrollment.');
      } else if (paymentData.module === 'Bus' && enrollmentSummary.bus.length === 0) {
        setValidationWarning('⚠️ Warning: This family has no Bus enrollments. You cannot record a Bus payment without an active Bus enrollment.');
      } else if (paymentData.module === 'Activities' && enrollmentSummary.activities.length === 0) {
        setValidationWarning('⚠️ Warning: This family has no active Activities enrollments with outstanding balance. You cannot record an Activities payment.');
      } else if (paymentData.module === 'Daycare' && getModuleBalance('Daycare') <= 0) {
        setValidationWarning('⚠️ Warning: This family has no outstanding Daycare balance. You cannot record a Daycare payment.');
      } else if (paymentData.module === 'ArtSchool' && getModuleBalance('ArtSchool') <= 0) {
        setValidationWarning('⚠️ Warning: This family has no outstanding Art School balance.');
      } else {
        setValidationWarning('');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentData.module, enrollmentSummary, selectedType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation for rental groups
    if (selectedType === 'group' && paymentData.module === 'BusRental') {
      if (!selectedTrip) {
        showError('Please select a trip to record payment.');
        return;
      }
    }
    
    // Validation: Cannot pay for Afterschool without enrollment
    if (paymentData.module === 'Afterschool' && enrollmentSummary && enrollmentSummary.afterschool.length === 0) {
      showError('Cannot record Afterschool payment: This family has no active Afterschool enrollments.');
      return;
    }
    
    // Validation: Cannot pay for Bus without enrollment
    if (paymentData.module === 'Bus' && enrollmentSummary && enrollmentSummary.bus.length === 0) {
      showError('Cannot record Bus payment: This family has no Bus enrollments.');
      return;
    }
    
    // Validation: Cannot pay for Activities without enrollment
    if (paymentData.module === 'Activities' && enrollmentSummary && enrollmentSummary.activities.length === 0) {
      showError('Cannot record Activities payment: This family has no active Activities enrollments with outstanding balance.');
      return;
    }
    
    // Validation: Cannot pay for Daycare without any outstanding balance
    if (paymentData.module === 'Daycare') {
      const totalBalance = getModuleBalance('Daycare');
      if (totalBalance <= 0) {
        showError('Cannot record Daycare payment: This family has no outstanding Daycare balance.');
        return;
      }
    }

    // Validation: Cannot pay for Art School without outstanding balance
    if (paymentData.module === 'ArtSchool') {
      const totalBalance = getModuleBalance('ArtSchool');
      if (totalBalance <= 0) {
        showError('Cannot record Art School payment: This family has no outstanding Art School balance.');
        return;
      }
    }
    
    try {
      setLoading(true);
      
      // ✅ Build notes from activities - store unique activity names only
      let notesText = paymentData.notes;
      let activityEnrollmentIds = undefined;
      
      // Bus Rental notes
      if (paymentData.module === 'BusRental' && selectedTrip) {
        notesText = `${selectedGroup.GroupName} - ${selectedTrip.Destination}`;
      }
      
      if (paymentData.module === 'Activities' && enrollmentSummary && enrollmentSummary.activities.length > 0) {
        // Get all activity enrollment IDs (system will split payment across all)
        activityEnrollmentIds = enrollmentSummary.activities.map(a => a.EnrollmentID);
        
        // ✅ Extract unique activity names for clean grouping in DailyReport
        const uniqueActivityNames = [...new Set(enrollmentSummary.activities.map(a => a.ActivityName))];
        notesText = uniqueActivityNames.join(', ');
      }

      // ArtSchool notes — build breakdown from active enrollments
      if (paymentData.module === 'ArtSchool' && enrollmentSummary?.artSchool?.enrollments?.length > 0) {
        // Format: "Drums (Private), Ballet (Group)" — class names for daily report breakdown
        const uniqueClasses = [...new Set(
          enrollmentSummary.artSchool.enrollments.map(e => `${e.ClassName} (${e.ClassType})`)
        )];
        notesText = uniqueClasses.join(', ');
      }
      
      const result = await apiCall('/payments', {
        method: 'POST',
        body: JSON.stringify({
          familyId: paymentData.module === 'BusRental' ? null : paymentData.familyId,
          module: paymentData.module,
          amount: parseFloat(paymentData.amount),
          paymentMethod: paymentData.paymentMethod,
          paymentDate: paymentData.paymentDate,
          academicYear: payYear || moduleYears[paymentData.module] || initialPayYear,
          notes: notesText,
          currency: 'USD',
          referenceNumber: paymentData.module === 'BusRental' ? `RENTAL-${selectedTrip.TripID}` : undefined,
          activityEnrollmentIds: activityEnrollmentIds // ✅ Pass all activity IDs for auto-split
        })
      });
      
      // Update trip payment status if Bus Rental
      if (paymentData.module === 'BusRental' && selectedTrip) {
        await apiCall(`/bus/rental-trips/${selectedTrip.TripID}/update-payment`, {
          method: 'POST'
        });
      }
      
      onSuccess(result.paymentId);
    } catch (error) {
      showError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Calculate module balances for display
  const getModuleBalance = (module) => {
    if (!enrollmentSummary) return 0;
    
    switch (module) {
      case 'Afterschool':
        return (enrollmentSummary.afterschool || []).reduce((sum, e) =>
          sum + (e.TrueBalance || e.BalanceRemaining || 0), 0
        );
      case 'Bus':
        return (enrollmentSummary.bus || []).reduce((sum, e) =>
          sum + (e.TrueBalance || e.BalanceRemaining || 0), 0
        );
      case 'Activities':
        return (enrollmentSummary.activities || []).reduce((sum, e) =>
          sum + (e.BalanceRemaining || 0), 0
        );
      case 'Daycare':
        // ✅ FIXED: Handle new object structure with totals
        if (enrollmentSummary.daycare?.totals) {
          return enrollmentSummary.daycare.totals.total || 0;
        }
        // Fallback for old structure (shouldn't happen)
        if (Array.isArray(enrollmentSummary.daycare)) {
          const enrollmentTotal = enrollmentSummary.daycare.reduce((sum, e) => 
            sum + (e.BalanceRemaining || 0), 0
          );
          return enrollmentTotal + oldDaycareBalance;
        }
        return 0;
      case 'ArtSchool':
        return enrollmentSummary.artSchool?.balance || 0;
      default:
        return 0;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-xl font-bold text-gray-800">Record Payment</h3>
            {selectedFamily && payYear && payYear !== initialPayYear && (
              <p className="text-xs text-amber-600 font-medium mt-0.5">Settling {payYear} balance</p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Search Family or Rental Group *</label>
            <div className="relative">
              <input
                type="text"
                value={familySearch}
                onChange={(e) => handleFamilySearch(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Type family name, phone, or rental group name..."
                autoComplete="off"
              />
              {(searchResults.families.length > 0 || searchResults.groups.length > 0) && (
                <div className="absolute mt-2 max-h-60 overflow-y-auto border border-gray-300 rounded-lg bg-white shadow-lg w-full z-30">
                  
                  {/* Family Results */}
                  {searchResults.families.map(family => (
                    <button
                      key={family.FamilyID}
                      type="button"
                      onClick={() => selectFamily(family)}
                      className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-gray-100 transition-colors"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">👨‍👩‍👧</span>
                          <div>
                            <p className="font-semibold text-gray-800">
                              {family.FamilyID} - {family.FatherName}
                            </p>
                            <p className="text-sm text-gray-600">
                              Family | Phone: {family.PrimaryPhone}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            (family.TotalOutstanding || 0) > 0 
                              ? 'bg-red-100 text-red-800' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            Balance: ${(family.TotalOutstanding || 0).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </button>
                  ))}
                  
                  {/* Rental Group Results */}
                  {searchResults.groups.map(group => (
                    <button
                      key={group.GroupID}
                      type="button"
                      onClick={() => selectGroup(group)}
                      className="w-full text-left px-4 py-3 hover:bg-purple-50 border-b border-gray-100 transition-colors"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">🚌</span>
                          <div>
                            <p className="font-semibold text-gray-800">
                              {group.GroupName}
                            </p>
                            <p className="text-sm text-gray-600">
                              Rental Group | {group.UnpaidTrips || 0} unpaid trip(s)
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="px-2 py-1 rounded text-xs font-semibold bg-purple-100 text-purple-800">
                            Balance: ${(group.TotalOutstanding || 0).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </button>
                  ))}
                  
                </div>
              )}
            </div>
          </div>

          {selectedFamily && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-blue-900 mb-2">Selected Family:</p>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <p className="text-sm text-blue-700">
                    <span className="font-semibold">ID:</span> {selectedFamily.FamilyID}
                  </p>
                  <p className="text-sm text-blue-700">
                    <span className="font-semibold">Father:</span> {selectedFamily.FatherName}
                  </p>
                  <p className="text-sm text-blue-700">
                    <span className="font-semibold">Phone:</span> {selectedFamily.PrimaryPhone}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-blue-900 mb-1">Outstanding Balance:</p>
                  <p className={`text-2xl font-bold ${
                    (selectedFamily.TotalOutstanding || 0) > 0 ? 'text-red-600' : 'text-green-600'
                  }`}>
                    ${(selectedFamily.TotalOutstanding || 0).toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Full module x year breakdown — click a line to pay it. FIFO: the
              oldest year must be settled before newer ones, except Daycare and
              Art School which aren't academic-year-bound. */}
          {selectedFamily && owedItems.length > 0 && (() => {
            const EXEMPT = new Set(['Daycare', 'ArtSchool']);
            const boundYears = owedItems.filter((i) => !EXEMPT.has(i.module)).map((i) => i.year);
            const oldestBound = boundYears.length ? boundYears.slice().sort()[0] : null;
            const isLocked = (item) => !EXEMPT.has(item.module) && oldestBound && item.year > oldestBound;
            return (
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-gray-900 mb-2">Balances owed — click one to pay:</p>
                {oldestBound && owedItems.some(isLocked) && (
                  <p className="text-xs text-amber-600 mb-2">
                    Settle {oldestBound} first (Daycare / Art School can be paid anytime).
                  </p>
                )}
                <div className="flex flex-col gap-1.5">
                  {owedItems.map((item) => {
                    const active = paymentData.module === item.module && payYear === item.year;
                    const locked = isLocked(item);
                    return (
                      <button
                        key={`${item.module}-${item.year}`}
                        type="button"
                        disabled={locked}
                        onClick={() => { if (!locked) pickOwed(item); }}
                        title={locked ? `Settle ${oldestBound} balances first` : undefined}
                        className={`flex justify-between items-center px-3 py-2 rounded-lg border text-sm text-left transition ${
                          locked ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                            : active ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <span className="font-medium text-gray-800">
                          {item.module} <span className="text-gray-500">· {item.year}</span>
                          {locked && <span className="ml-1 text-[10px] text-gray-400">🔒 locked</span>}
                        </span>
                        <span className="font-bold text-red-600">${item.balance.toFixed(2)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {enrollmentSummary && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-gray-900 mb-3">Current Enrollments:</p>
              <div className="grid md:grid-cols-4 gap-4">
                {/* Afterschool */}
                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-2">Afterschool:</p>
                  {enrollmentSummary.afterschool?.length > 0 ? (
                    <div className="space-y-1">
                      {enrollmentSummary.afterschool.map((e, idx) => (
                        <div key={idx} className="text-xs bg-white rounded p-2 border border-gray-200">
                          <p className="font-semibold">{e.FirstName} {e.LastName}</p>
                          <p className="text-gray-600">{e.PackageName} - {e.BillingType}</p>
                          <p className="text-red-600">Balance: ${(e.TrueBalance || e.BalanceRemaining || 0).toFixed(2)}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 italic">No active enrollments</p>
                  )}
                </div>

                {/* Bus */}
                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-2">Bus:</p>
                  {enrollmentSummary.bus?.length > 0 ? (
                    <div className="space-y-1">
                      {enrollmentSummary.bus.map((e, idx) => (
                        <div key={idx} className="text-xs bg-white rounded p-2 border border-gray-200">
                          <p className="font-semibold">{e.StudentName}</p>
                          <p className="text-gray-600">Line {e.LineNumber} - T{e.Trimester}</p>
                          <p className={`${e.PaymentStatus === 'Paid' ? 'text-green-600' : 'text-red-600'}`}>
                            {e.PaymentStatus}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 italic">No bus enrollments</p>
                  )}
                </div>

                {/* Activities */}
                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-2">Activities (with balance):</p>
                  {enrollmentSummary.activities?.length > 0 ? (
                    <div className="space-y-1">
                      {enrollmentSummary.activities.map((e, idx) => (
                        <div key={idx} className="text-xs bg-white rounded p-2 border border-gray-200">
                          <p className="font-semibold">{e.FirstName} {e.LastName}</p>
                          <p className="text-gray-600">{e.ActivityName}</p>
                          <p className="text-gray-500 text-xs">{e.CategoryName}</p>
                          <p className="text-red-600">Balance: ${(e.BalanceRemaining || 0).toFixed(2)}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 italic">No activities with balance</p>
                  )}
                </div>
                
                {/* Daycare */}
                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-2">Daycare:</p>
                  {enrollmentSummary.daycare?.enrollments && enrollmentSummary.daycare.enrollments.length > 0 ? (
                    <div className="space-y-2">
                      {/* Enrollments */}
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-gray-600">Active Enrollments:</p>
                        {enrollmentSummary.daycare.enrollments.map((e, idx) => (
                          <div key={idx} className="text-xs bg-white rounded p-2 border border-gray-200">
                            <p className="font-semibold">{e.FirstName} {e.LastName}</p>
                            <p className="text-gray-600">{e.PackageName}</p>
                            <p className="text-gray-500 text-xs">{e.TimeSlot}</p>
                          </div>
                        ))}
                      </div>

                      {/* Charges Summary */}
                      {enrollmentSummary.daycare.totals && enrollmentSummary.daycare.totals.total > 0 && (
                        <div className="bg-blue-50 border border-blue-200 rounded p-2">
                          <p className="text-xs font-semibold text-blue-900 mb-1">Outstanding Charges:</p>
                          
                          {enrollmentSummary.daycare.totals.oldBalance > 0 && (
                            <div className="flex justify-between text-xs text-gray-700">
                              <span>Old Balance:</span>
                              <span className="font-semibold text-red-600">
                                ${enrollmentSummary.daycare.totals.oldBalance.toFixed(2)}
                              </span>
                            </div>
                          )}
                          
                          {enrollmentSummary.daycare.totals.additional > 0 && (
                            <div className="flex justify-between text-xs text-gray-700">
                              <span>Additional Charges:</span>
                              <span className="font-semibold text-orange-600">
                                ${enrollmentSummary.daycare.totals.additional.toFixed(2)}
                              </span>
                            </div>
                          )}
                          
                          {enrollmentSummary.daycare.totals.monthly > 0 && (
                            <div className="flex justify-between text-xs text-gray-700">
                              <span>Monthly Charges:</span>
                              <span className="font-semibold text-blue-600">
                                ${enrollmentSummary.daycare.totals.monthly.toFixed(2)}
                              </span>
                            </div>
                          )}
                          
                          <div className="border-t border-blue-300 mt-1 pt-1 flex justify-between text-xs font-bold">
                            <span>Total Balance:</span>
                            <span className="text-red-600">
                              ${enrollmentSummary.daycare.totals.total.toFixed(2)}
                            </span>
                          </div>

                          {/* Detailed Charges List */}
                          {enrollmentSummary.daycare.chargesByType && (
                            <div className="mt-2 space-y-1">
                              <details className="text-xs">
                                <summary className="cursor-pointer text-blue-700 hover:text-blue-900 font-semibold">
                                  View Charge Details
                                </summary>
                                <div className="mt-1 space-y-1 pl-2">
                                  {/* Old Balance */}
                                  {enrollmentSummary.daycare.chargesByType.oldBalance?.length > 0 && (
                                    <div>
                                      <p className="font-semibold text-red-700">Old Balance:</p>
                                      {enrollmentSummary.daycare.chargesByType.oldBalance.map((charge, idx) => (
                                        <div key={idx} className="text-xs bg-red-50 rounded p-1 mt-1">
                                          <p className="text-gray-700">{charge.Description}</p>
                                          <p className="text-red-600 font-semibold">${charge.RemainingBalance.toFixed(2)}</p>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {/* Additional Charges */}
                                  {enrollmentSummary.daycare.chargesByType.additional?.length > 0 && (
                                    <div>
                                      <p className="font-semibold text-orange-700">Additional Charges:</p>
                                      {enrollmentSummary.daycare.chargesByType.additional.map((charge, idx) => (
                                        <div key={idx} className="text-xs bg-orange-50 rounded p-1 mt-1">
                                          <div className="flex justify-between">
                                            <span className="text-gray-700">{charge.ChargeType}</span>
                                            <span className="text-orange-600 font-semibold">
                                              ${charge.RemainingBalance.toFixed(2)}
                                            </span>
                                          </div>
                                          <p className="text-gray-500">{charge.ChildName}</p>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {/* Monthly Charges */}
                                  {enrollmentSummary.daycare.chargesByType.monthly?.length > 0 && (
                                    <div>
                                      <p className="font-semibold text-blue-700">Monthly Charges:</p>
                                      {enrollmentSummary.daycare.chargesByType.monthly.map((charge, idx) => (
                                        <div key={idx} className="text-xs bg-blue-50 rounded p-1 mt-1">
                                          <div className="flex justify-between">
                                            <span className="text-gray-700">
                                              {charge.ChargeMonth}/{charge.ChargeYear}
                                            </span>
                                            <span className="text-blue-600 font-semibold">
                                              ${charge.RemainingBalance.toFixed(2)}
                                            </span>
                                          </div>
                                          <p className="text-gray-500">{charge.ChildName}</p>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </details>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 italic">No active enrollments</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Art School Summary */}
          {enrollmentSummary && enrollmentSummary.artSchool?.enrollments?.length > 0 && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
              <p className="text-xs font-semibold text-indigo-700 mb-2">Art School:</p>
              <div className="space-y-1">
                {enrollmentSummary.artSchool.enrollments.map((e, idx) => (
                  <div key={idx} className="text-xs bg-white rounded p-2 border border-indigo-100 flex justify-between items-center">
                    <div>
                      <p className="font-semibold text-indigo-900">{e.StudentName}</p>
                      <p className="text-indigo-600">{e.ClassName} ({e.ClassType}) — {e.BillingType}</p>
                    </div>
                    {parseFloat(e.BalanceRemaining) > 0 && (
                      <span className="text-red-600 font-semibold">${parseFloat(e.BalanceRemaining).toFixed(2)}</span>
                    )}
                  </div>
                ))}
                <div className="flex justify-between text-xs font-bold text-indigo-800 pt-1 border-t border-indigo-200">
                  <span>Total Balance:</span>
                  <span className="text-red-600">${enrollmentSummary.artSchool.balance.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {selectedGroup && (
            <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-2xl">🚌</span>
                <div>
                  <p className="text-sm font-semibold text-purple-900">Selected Rental Group:</p>
                  <p className="font-bold text-purple-900">{selectedGroup.GroupName}</p>
                  {selectedGroup.ContactPerson && (
                    <p className="text-xs text-purple-700">Contact: {selectedGroup.ContactPerson}</p>
                  )}
                </div>
              </div>
              
              {groupTrips.length > 0 ? (
                <>
                  <p className="text-sm font-semibold text-purple-900 mb-2">
                    Select Trip to Pay:
                  </p>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {groupTrips.map(trip => (
                      <label
                        key={trip.TripID}
                        className={`block p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                          selectedTrip?.TripID === trip.TripID
                            ? 'border-purple-600 bg-purple-100'
                            : 'border-purple-200 hover:border-purple-300 bg-white'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <input
                            type="radio"
                            name="trip"
                            checked={selectedTrip?.TripID === trip.TripID}
                            onChange={() => selectTrip(trip)}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900">
                              Trip #{trip.TripID} - {trip.Destination}
                            </div>
                            <div className="text-xs text-gray-600 mt-1">
                              📅 {new Date(trip.TripDate).toLocaleDateString()} | 
                              🚌 {trip.NumberOfBuses} bus(es) | 
                              👥 {trip.NumberOfPassengers || '?'} passengers
                            </div>
                            <div className="flex justify-between items-center mt-2 pt-2 border-t border-purple-200">
                              <span className="text-sm text-gray-600">Trip Total:</span>
                              <span className="text-sm font-semibold">${trip.FinalPrice?.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center mt-1">
                              <span className="text-sm text-gray-600">Already Paid:</span>
                              <span className="text-sm font-semibold">${trip.AmountPaid?.toFixed(2) || '0.00'}</span>
                            </div>
                            <div className="flex justify-between items-center mt-1">
                              <span className="text-sm font-bold text-red-600">Balance Due:</span>
                              <span className="text-lg font-bold text-red-600">${trip.BalanceRemaining?.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-sm text-purple-700">
                  ✅ No unpaid trips for this group.
                </p>
              )}
            </div>
          )}

          {validationWarning && (
            <div className={`border-2 rounded-lg p-4 flex items-start gap-3 ${
              (paymentData.module === 'Afterschool' && enrollmentSummary?.afterschool.length === 0) ||
              (paymentData.module === 'Bus' && enrollmentSummary?.bus.length === 0) ||
              (paymentData.module === 'Activities' && enrollmentSummary?.activities.length === 0)
                ? 'bg-red-50 border-red-300'
                : 'bg-yellow-50 border-yellow-300'
            }`}>
              <AlertCircle className={`flex-shrink-0 mt-0.5 ${
                (paymentData.module === 'Afterschool' && enrollmentSummary?.afterschool.length === 0) ||
                (paymentData.module === 'Bus' && enrollmentSummary?.bus.length === 0) ||
                (paymentData.module === 'Activities' && enrollmentSummary?.activities.length === 0)
                  ? 'text-red-600'
                  : 'text-yellow-600'
              }`} size={20} />
              <p className={`text-sm font-semibold ${
                (paymentData.module === 'Afterschool' && enrollmentSummary?.afterschool.length === 0) ||
                (paymentData.module === 'Bus' && enrollmentSummary?.bus.length === 0) ||
                (paymentData.module === 'Activities' && enrollmentSummary?.activities.length === 0)
                  ? 'text-red-800'
                  : 'text-yellow-800'
              }`}>
                {validationWarning}
              </p>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Module *</label>
              <select
                required
                value={paymentData.module}
                onChange={(e) => setPaymentData({...paymentData, module: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                disabled={!selectedFamily && !selectedGroup}
              >
                <option value="Afterschool">
                  Afterschool {enrollmentSummary ? `($${getModuleBalance('Afterschool').toFixed(2)} due)` : ''}
                </option>
                <option value="Bus">
                  Bus {enrollmentSummary ? `($${getModuleBalance('Bus').toFixed(2)} due)` : ''}
                </option>
                <option value="Activities">
                  Activities {enrollmentSummary ? `($${getModuleBalance('Activities').toFixed(2)} due)` : ''}
                </option>
                <option value="Daycare">
                  Daycare {enrollmentSummary ? `($${getModuleBalance('Daycare').toFixed(2)} due)` : ''}
                </option>
                <option value="ArtSchool">
                  Art School {enrollmentSummary ? `($${getModuleBalance('ArtSchool').toFixed(2)} due)` : ''}
                </option>
                <option value="BusRental">
                  Bus Rental {selectedTrip ? `($${selectedTrip.BalanceRemaining?.toFixed(2)} due)` : ''}
                </option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Amount ($) *</label>
              <input
                type="number"
                step="0.01"
                required
                value={paymentData.amount}
                onChange={(e) => setPaymentData({...paymentData, amount: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="0.00"
                disabled={!selectedFamily}
              />
            </div>
          </div>

          {/* ✅ NEW: Show info when Activities module is selected */}
          {paymentData.module === 'Activities' && enrollmentSummary && enrollmentSummary.activities?.length > 0 && (
            <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-4">
              <p className="text-sm font-semibold text-orange-900 mb-2">
                💡 Payment Allocation
              </p>
              <p className="text-xs text-orange-800 mb-3">
                Payment will be automatically split proportionally across all {enrollmentSummary.activities.length} activity enrollment(s) based on their outstanding balances:
              </p>
              <div className="space-y-1">
                {enrollmentSummary.activities.map((activity, idx) => (
                  <div key={idx} className="flex justify-between text-xs bg-white rounded p-2">
                    <span className="text-gray-700">
                      {activity.FirstName} {activity.LastName} - {activity.ActivityName}
                    </span>
                    <span className="font-semibold text-red-600">
                      ${(activity.BalanceRemaining || 0).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-orange-700 mt-3 font-semibold">
                Total Due: ${getModuleBalance('Activities').toFixed(2)}
              </p>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Method *</label>
              <select
                required
                value={paymentData.paymentMethod}
                onChange={(e) => setPaymentData({...paymentData, paymentMethod: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                disabled={!selectedFamily}
              >
                <option value="Cash">Cash</option>
                <option value="Check">Check</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Credit Card">Credit Card</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Date *</label>
              <input
                type="date"
                required
                value={paymentData.paymentDate}
                onChange={(e) => setPaymentData({...paymentData, paymentDate: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                disabled={!selectedFamily}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Additional Notes {paymentData.module === 'Activities' && '(Optional - Activity details auto-added)'}
            </label>
            <textarea
              value={paymentData.notes}
              onChange={(e) => setPaymentData({...paymentData, notes: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              rows="2"
              placeholder={paymentData.module === 'Activities' ? 'Optional additional notes...' : 'Optional payment notes...'}
              disabled={!selectedFamily}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button 
              type="submit" 
              disabled={
                loading || 
                (!selectedFamily && !selectedGroup) ||
                (selectedGroup && !selectedTrip) ||
                (paymentData.module === 'Afterschool' && enrollmentSummary?.afterschool.length === 0) ||
                (paymentData.module === 'Bus' && enrollmentSummary?.bus.length === 0) ||
                (paymentData.module === 'Activities' && enrollmentSummary?.activities.length === 0)
              }
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Recording...' : 'Record Payment'}
            </button>
            <button 
              type="button" 
              onClick={onClose} 
              className="px-6 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 rounded-lg"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentModal;